import { createNgModule, DOCUMENT, Inject, Injectable, Injector } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import { finalize, from, Observable, switchMap } from "rxjs";
import { SHOPIFY_HOST } from "../../constants";
import { AppDialogs, AppQueryStrings, AppRoutes } from "../../enums";
import { isDialogHostPath } from "../../utils/dialog-host/dialog-host";
import { ShopifyAppBridgeService } from "../shopify-app-bridge/shopify-app-bridge.service";
import { ToastService } from "../toast/toast.service";
import { DialogAction, DialogMessage, DialogOptions, DialogSize, DialogState, DialogTypes } from "./dialog.interface";
import { DIALOGS } from "./dialog.registry";

/**
 * How long the page waits for Shopify to confirm it closed a modal before taking it as closed. The Shopify mobile app
 * closes it without firing `hide` on its element, and the page must not keep a closed modal as open (no other one opens).
 */
const MODAL_HIDE_TIMEOUT_MS = 1500;

/** Polaris Modal widths (@shopify/polaris 13.9.5), the sizes Shopify's modal has inside the admin. */
const MODAL_WIDTHS: Record<Exclude<DialogSize, "max">, string> = {
  small: "380px",
  base: "620px",
  large: "min(980px, calc(100vw - 64px))",
};

/**
 * Opens our dialogs. Inside the Shopify admin they open as a Shopify modal that loads `/:programSlug/dialog/<key>`,
 * where the same Angular component runs in its own frame; outside Shopify they open with MatDialog as always.
 * Either way the caller gets the dialog result (`undefined` when dismissed) and the Observable completes.
 */
@Injectable({ providedIn: "root" })
export class DialogService {
  constructor(
    private readonly matDialog: MatDialog,
    private readonly injector: Injector,
    private readonly translateService: TranslateService,
    private readonly shopifyAppBridgeService: ShopifyAppBridgeService,
    private readonly toastService: ToastService,
    @Inject(DOCUMENT) private readonly document: Document,
  ) {}

  public open<K extends AppDialogs>(
    key: K,
    data: DialogTypes[K]["data"],
    options: DialogOptions = {},
  ): Observable<DialogTypes[K]["result"] | undefined> {
    // A dialog opened from a dialog already inside a Shopify modal stays a MatDialog there: Shopify does not stack modals.
    // In the Shopify mobile app dialogs open in the app: it opened the first Shopify modal of a page and no later one.
    if (this.shopifyAppBridgeService.isEmbedded && !window.shopify.environment.mobile && !isDialogHostPath() && !this.matDialog.openDialogs?.length) {
      return this.openShopifyModal(key, data);
    }
    return this.openMatDialog(key, data, options);
  }

  private openMatDialog<K extends AppDialogs>(
    key: K,
    data: DialogTypes[K]["data"],
    options: DialogOptions,
  ): Observable<DialogTypes[K]["result"] | undefined> {
    const definition = DIALOGS[key];

    return from(definition.load()).pipe(
      switchMap(({ component, module }) => {
        const moduleRef = module ? createNgModule(module, options.injector ?? this.injector) : null;
        return this.matDialog
          .open<DialogTypes[K]["component"], DialogTypes[K]["data"], DialogTypes[K]["result"]>(component, {
            maxWidth: "100vw",
            ...definition.matDialogConfig,
            width: definition.size === "max" ? definition.width : MODAL_WIDTHS[definition.size],
            data,
            injector: moduleRef?.injector,
          })
          .afterClosed()
          .pipe(finalize(() => moduleRef?.destroy()));
      }),
    );
  }

  private openShopifyModal<K extends AppDialogs>(key: K, data: DialogTypes[K]["data"]): Observable<DialogTypes[K]["result"] | undefined> {
    return new Observable<DialogTypes[K]["result"] | undefined>(subscriber => {
      const dialogId = crypto.randomUUID();
      const origin = window.location.origin;
      let dialogWindow: Window | null = null;
      let result: DialogTypes[K]["result"] | undefined;
      let open = true;
      let closing = false;
      let hideTimeout: ReturnType<typeof setTimeout> | undefined;

      const sendAction = (action: "primary" | "secondary"): void => {
        dialogWindow?.postMessage({ type: "dialog-action", dialogId, action } satisfies DialogMessage, origin);
      };
      const modal = this.prepareModal(key, data, dialogId, sendAction);
      const titleBar = modal.querySelector<HTMLElement>("ui-title-bar");
      const buttons = Array.from(titleBar.querySelectorAll("button"));

      const onMessage = (event: MessageEvent<DialogMessage>): void => {
        if (event.origin !== origin || event.data?.dialogId !== dialogId) return;

        if (event.data.type === "dialog-session-token-request") {
          const { requestId } = event.data;
          const source = event.source as Window;
          void window.shopify
            .idToken()
            .then(
              token => source.postMessage({ type: "dialog-session-token", dialogId, requestId, token } satisfies DialogMessage, origin),
              // Without a token the modal's admin cannot start: close it, as its close button would, rather than leave it blank
              () => close(),
            );
        } else if (event.data.type === "dialog-ready") {
          dialogWindow = event.source as Window;
          dialogWindow.postMessage({ type: "dialog-data", dialogId, data } satisfies DialogMessage, origin);
        } else if (event.data.type === "dialog-state") {
          this.applyState(titleBar, buttons, event.data.state);
        } else if (event.data.type === "dialog-toast") {
          // The modal frame has no App Bridge toasts and may not be allowed to download: the page does both
          this.toastService.show({ title: event.data.title, message: event.data.message, duration: event.data.duration });
        } else if (event.data.type === "dialog-download") {
          const link = this.document.createElement("a");
          link.download = event.data.filename;
          link.href = event.data.href;
          link.click();
        } else if (event.data.type === "dialog-close" && !closing) {
          // The first close carries the result; the frame also reports one when it unloads (a swipe dismissing it)
          result = event.data.result;
          close();
        }
      };
      // Shopify hides the modal on our close message, its close button, Escape or a click outside it. On the desktop it
      // fires `hide`; the mobile app does not, so its hide() resolving (or a short wait) also ends the modal, once.
      const onHide = (): void => {
        if (!open) return;
        clearTimeout(hideTimeout);
        open = false;
        subscriber.next(result);
        subscriber.complete();
      };
      const close = (): void => {
        if (closing) return;
        closing = true;
        hideTimeout = setTimeout(onHide, MODAL_HIDE_TIMEOUT_MS);
        void window.shopify.modal.hide(modal.id).then(onHide, onHide);
      };

      window.addEventListener("message", onMessage);
      modal.addEventListener("hide", onHide);
      void window.shopify.modal.show(modal.id);

      return () => {
        clearTimeout(hideTimeout);
        window.removeEventListener("message", onMessage);
        modal.removeEventListener("hide", onHide);
        // The modal stays in the page, closed, for the next time this dialog opens
        if (open) void window.shopify.modal.hide(modal.id);
      };
    });
  }

  /** Mirrors the dialog's own title and buttons on Shopify's: disabled, loading (Shopify's spinner), hidden or relabelled. */
  private applyState(titleBar: HTMLElement, buttons: HTMLButtonElement[], state: DialogState): void {
    if (state.heading) titleBar.setAttribute("title", this.translateService.instant(state.heading));

    for (const button of buttons) {
      const buttonState = state[button.dataset["dialogAction"] as "primary" | "secondary"];
      // States are partial: a button no state has mentioned yet was only waiting for the dialog to load (Cancel, Dismiss)
      if (!buttonState) {
        if (button.dataset["awaitingState"]) button.disabled = false;
        delete button.dataset["awaitingState"];
        continue;
      }
      delete button.dataset["awaitingState"];

      button.disabled = !!buttonState.disabled;
      button.toggleAttribute("loading", !!buttonState.loading);
      button.hidden = !!buttonState.hidden;
      if (buttonState.label) button.textContent = this.translateService.instant(buttonState.label);
    }

    // Shopify draws every button in the title bar, so a hidden one leaves it until it shows again
    const visible = buttons.filter(button => !button.hidden);
    if (visible.some(button => !button.isConnected) || titleBar.childElementCount !== visible.length) titleBar.replaceChildren(...visible);
  }

  /**
   * The dialog's `<ui-modal src variant><ui-title-bar title><button variant="primary"/><button/></ui-title-bar></ui-modal>`,
   * set up for this opening. As Shopify's docs use it, each dialog has one modal in the page, with a stable id, shown
   * and hidden by that id: only its route (a new dialog id), title and buttons change. The Shopify mobile app opened the
   * first modal of a page and none created after it.
   */
  private prepareModal<K extends AppDialogs>(
    key: K,
    data: DialogTypes[K]["data"],
    dialogId: string,
    sendAction: (action: "primary" | "secondary") => void,
  ): HTMLElement {
    const definition = DIALOGS[key];
    // Inside the modal App Bridge has no config: the shop, host and admin locale travel in the URL, as on app launch
    const { shop, host, locale } = window.shopify.config;
    const params = new URLSearchParams({ shop, host, locale, [AppQueryStrings.dialogId]: dialogId });

    const id = `app-dialog-${key}`;
    let modal = this.document.getElementById(id);
    if (!modal) {
      modal = this.document.createElement("ui-modal");
      modal.id = id;
      this.document.body.appendChild(modal);
    }
    modal.setAttribute("src", `/${shop.replace(SHOPIFY_HOST, "")}/${AppRoutes.dialog}/${key}?${params}`);
    modal.setAttribute("variant", definition.size);

    const titleBar = this.document.createElement("ui-title-bar");
    titleBar.setAttribute("title", this.translateService.instant(definition.heading(data)));
    const addButton = (action: DialogAction<K> | undefined, name: "primary" | "secondary"): void => {
      if (!action || action.visible?.(data) === false) return;
      const button = this.document.createElement("button");
      button.dataset["dialogAction"] = name;
      // Disabled until the dialog has loaded and reports its state: a click before then would reach no dialog and be lost
      button.disabled = true;
      button.dataset["awaitingState"] = "true";
      button.textContent = this.translateService.instant(action.label(data));
      if (name === "primary") button.setAttribute("variant", "primary");
      if (action.tone?.(data) === "critical") button.setAttribute("tone", "critical");
      button.addEventListener("click", () => sendAction(name));
      titleBar.appendChild(button);
    };
    addButton(definition.primaryAction, "primary");
    addButton(definition.secondaryAction, "secondary");
    modal.querySelector("ui-title-bar")?.remove();
    modal.appendChild(titleBar);

    return modal;
  }
}
